/**
 * Тонкая абстракция над средой запуска.
 * Мобильная версия — тот же билд в WebView; если появится нативный bridge,
 * меняется только эта реализация.
 */
export function isWebView(): boolean {
  if (typeof navigator === 'undefined') return false
  return /wv|WebView/i.test(navigator.userAgent)
}

export function isTouchDevice(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches
}

/** Скачивание файла. В WebView обёртка обязана поддержать download. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.append(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

export function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer')
}

/**
 * navigator.userAgentData (Client Hints) в типах lib.dom нет — читаем как внешние данные,
 * без каста и без any: Reflect.get отдаёт unknown-совместимое значение, тип сужаем проверкой.
 */
function readUserAgentDataPlatform(): string | undefined {
  const data: unknown = Reflect.get(navigator, 'userAgentData')
  if (typeof data !== 'object' || data === null) return undefined

  const platform: unknown = Reflect.get(data, 'platform')
  return typeof platform === 'string' && platform !== '' ? platform : undefined
}

/**
 * Есть ли на клавиатуре ⌘. На Windows и Linux главный модификатор — Ctrl, и подсказка с ⌘
 * там просто врёт.
 *
 * navigator.platform не используется: помечен устаревшим и на iPadOS отдаёт MacIntel.
 * Порядок источников — Client Hints (Chromium 90+, включая Android WebView), затем строка UA
 * (Safari и Firefox Client Hints не поддерживают). iPadOS в desktop-режиме присылает Macintosh,
 * и это верный ответ: у iPad с клавиатурой Apple ⌘ настоящая.
 *
 * Вне браузера (SSR, воркер) — false, то есть Ctrl: парк рабочих мест B2B-портала — Windows,
 * ошибаться лучше в сторону большинства. Значение детерминированное, поэтому появись когда-нибудь
 * серверный рендер — разметка сервера и клиента совпадут.
 *
 * Функция должна остаться единственным источником истины: обработчик ускорителя обязан
 * проверять metaKey/ctrlKey по ней же, иначе подпись и реакция разъедутся.
 */
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false

  const uaPlatform = readUserAgentDataPlatform()
  if (uaPlatform !== undefined) return /mac|ios|iphone|ipad/i.test(uaPlatform)

  return /mac|iphone|ipad|ipod/i.test(navigator.userAgent)
}
